import { verifyAgent } from '@/apis/agent';
import { getPremiumQuotation } from '@/apis/application';
import { documents } from '@/apis/documents';
import {
  AGENT_BRANCH,
  AGENT_CODE_INSURANCE,
  ECHANNEL_APP_ID,
  FUND_OTHERS,
  GENDER_FEMALE,
  GENDER_MALE,
  MARITAL_STATUS_OTHERS,
  PRE_PHONE_NUMBER,
  SELF_ID,
} from '@/constants';
import { AgentStatus } from '@/enums/AgentStatus';
import { AnswerEnum } from '@/enums/AnswerEnum';
import { ClientType } from '@/enums/ClientType';
import { DistributionChannel } from '@/enums/DistributionChannel';
import { PaymentMode } from '@/enums/PaymentMode';
import { PlanType } from '@/enums/PlanType';
import { Relationship } from '@/enums/Relationship';
import { TitleEnum } from '@/enums/TitleEnum';
import {
  capitalizeFirstChar,
  capitalizeFirstLetterOfEachWord,
  setDOB,
} from '@/helpers/InputKeyPressValidator';
import {
  ApplicantDetails,
  ApplicantPersonalInfo,
} from '@/types/ApplicantDetails';
import {
  Addresses,
  PurposeOfTransaction,
  UploadedNRIC,
} from '@/types/ApplicantDetails/ApplicantPersonalInfo';
import {
  ApplicationsResponseWrapper,
  GetApplicationResponseWrapper,
} from '@/types/Applications';
import { ApplicationsResponse } from '@/types/Applications/ApplicationsResponse';
import {
  Collection,
  GetApplicationsResponse,
  Suppression,
} from '@/types/Applications/GetApplicationsResponse';
import { PlanData, PlanDataDeductible } from '@/types/Plan';
import { PlanSummaryState } from '@/types/PlanSummary';
import { PromoCodeForm } from '@/types/PromoCode/PromoCodeForm';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

export const initialState: PlanSummaryState = {
  loading: false,
  error: false,
  appId: ECHANNEL_APP_ID,
  referenceId: '',
  productCode: '',
  paymentMode: '',
  distributionChannel: '',
  preferredLang: '',
  promoCode: '',
  agent: {
    agentCode: AGENT_CODE_INSURANCE,
    agentBranch: AGENT_BRANCH,
  },
  employee: undefined,
  marketingConsent: AnswerEnum.No,
  declaration: false,
  consentConfirmation: false,
  collection: undefined,
  payout: {
    source: '',
    accountHolderName: '',
    bankCode: '',
    accountNo: '',
  },
  contacts: {
    type: '',
    value: '',
    isPrimary: '',
  },
  suppression: undefined,
  applicants: [
    {
      id: 0,
      clientType: '',
      planCode: '',
      sumAssured: 0,
      relationship: '',
      idType: '',
      idNo: '',
      gender: '',
      occupationCode: '',
      occupationDesc: '',
      deductibleAmount: 0,
      name: {
        fullName: '',
        firstName: '',
        lastName: '',
        middleName: '',
        nickName: '',
      },
      dob: '',
      isSmoking: null,
      emails: [
        {
          type: '',
          value: '',
          isPrimary: '',
        },
      ],
      benePercentage: 0,
      isBumiputra: false,
      isStaff: false,
      isMaybankAcctHolder: false,
      isBeneficialOwner: false,
      title: '',
      pdpaConsent: '',
      race: '',
      religion: '',
      nationality: '',
      monthlyIncome: undefined,
      maritalStatus: '',
      educationLevel: '',
      companyName: '',
      contacts: {
        type: '',
        value: '',
        isPrimary: '',
      },
      addresses: [],
    },
  ],
  family: undefined,
  quickQuoteData: {
    appId: '',
    refId: '',
    productCode: '',
    paymentMode: PaymentMode.Annually.toString(),
    distributionChannel: '',
    preferredLang: '',
    applicantData: [],
    planSelection: PlanType.Family,
    family: undefined,
  },
  planData: {
    planPremium: [],
    applicants: [],
    monthlyTotalPremium: null,
    yearlyTotalPremium: null,
  },
  planDataDeductible: {
    planPremium: [],
    applicants: [],
    monthlyTotalPremium: null,
    yearlyTotalPremium: null,
  },
  premiumAmount: {
    monthlyTotalPremium: 0,
    yearlyTotalPremium: 0,
  },
  applicationsApplicants: undefined,
};

const planSummarySlice = createSlice({
  name: 'planSummary',
  initialState,
  reducers: {
    updateProductCode: (state, action) => {
      state.productCode = action.payload;
    },
    updateAgentCode: (
      state,
      action: PayloadAction<{ agentCode: string; agentBranch?: string }>,
    ) => {
      state.agent.agentCode = action.payload.agentCode;
      if (action.payload.agentBranch) {
        state.agent.agentBranch = action.payload.agentBranch;
      }
    },
    updateApplicantDetails: (
      state,
      action: PayloadAction<ApplicantDetails>,
    ) => {
      const arr: ApplicantDetails[] = state.quickQuoteData.applicantData;

      if (action.payload.relationship === Relationship.Self) {
        if (!arr.some((app) => app.relationship === Relationship.Self)) {
          arr.push(action.payload);
          state.quickQuoteData.applicantData = arr;
        }
      } else {
        arr.push(action.payload);
        state.quickQuoteData.applicantData = arr;
      }
    },
    removeApplicant: (
      state,
      action: PayloadAction<ApplicantDetails | ApplicantDetails[]>,
    ) => {
      let arr: ApplicantDetails[] = state.quickQuoteData.applicantData;
      if (!Array.isArray(action.payload)) {
        const payload: ApplicantDetails = action.payload;
        if (payload.id && payload.id !== SELF_ID) {
          arr = state.quickQuoteData.applicantData.filter(
            (applicant) =>
              applicant.id !== payload.id ||
              applicant.relationship === Relationship.Self,
          );
          state.quickQuoteData.applicantData = arr;
        } else if (payload.relationship !== Relationship.Self) {
          arr = state.quickQuoteData.applicantData.filter(
            (applicant) =>
              applicant.relationship !== payload.relationship ||
              applicant.gender !== payload.gender,
          );
          state.quickQuoteData.applicantData = arr;
        }
      } else {
        const idsToRemove = action.payload.map((app) => app.id);
        arr = state.quickQuoteData.applicantData.filter(
          (applicant) => !idsToRemove.includes(applicant.id),
        );
        state.quickQuoteData.applicantData = arr;
      }
      if (state.quickQuoteData.applicantData.length === 1) {
        delete state.family;
        delete state.quickQuoteData.family;
      }
    },

    removeChildApplicant: (state, action: PayloadAction<ApplicantDetails>) => {
      const arr: ApplicantDetails[] = state.quickQuoteData.applicantData;

      const indexToRemove = arr.findIndex(
        (applicant) =>
          applicant.relationship === action.payload.relationship &&
          applicant.gender === action.payload.gender,
      );

      // If a matching applicant is found, remove it
      if (indexToRemove !== -1) {
        arr.splice(indexToRemove, 1);
      }

      state.quickQuoteData.applicantData = arr;
    },

    updateQuoteFormDetail: (state, action: PayloadAction<ApplicantDetails>) => {
      const data: ApplicantDetails[] = state.quickQuoteData.applicantData;
      if (data) {
        const index = data.findIndex(
          (applicant) => applicant.id === action.payload.id,
        );

        if (index !== -1) {
          // Create a new array with updated data to avoid mutating the state directly
          state.quickQuoteData.applicantData = [
            ...data.slice(0, index),
            {
              ...data[index],
              ...action.payload,
            },
            ...data.slice(index + 1),
          ];
        }
      }
    },
    addPlanData: (state, action: PayloadAction<PlanData>) => {
      state.planData = action.payload;
    },
    addPlanDataDeductible: (
      state,
      action: PayloadAction<PlanDataDeductible>,
    ) => {
      state.planDataDeductible = action.payload;
    },
    updatePlanSelection: (state, action: PayloadAction<number>) => {
      // if (state.quickQuoteData.planSelection) {
      state.quickQuoteData.planSelection = action.payload;
    },
    updatePaymentMode: (state, action: PayloadAction<string>) => {
      // if (state.quickQuoteData.planSelection) {
      state.quickQuoteData.paymentMode = action.payload;
    },
    updateconsentConfirmation: (state, action: PayloadAction<boolean>) => {
      state.consentConfirmation = action.payload;
    },
    updatePlanCode: (state, action: PayloadAction<ApplicantDetails>) => {
      const data: ApplicantDetails[] = state.quickQuoteData.applicantData;

      if (data) {
        const index = data.findIndex(
          (applicant) => applicant.id === action.payload.id,
        );

        if (index !== -1) {
          data[index] = {
            ...data[index],
            planCode: action.payload.planCode,
          };
        }
      }
    },
    updateAllPlanCode: (state, action: PayloadAction<any>) => {
      const data: ApplicantDetails[] = state.quickQuoteData.applicantData;
      if (data) {
        data.forEach((applicant) => {
          applicant.planCode = action.payload;
        });
      }
    },
    updateDeductibleAmount: (
      state,
      action: PayloadAction<ApplicantDetails>,
    ) => {
      const updateDeductible = (arr?: ApplicantDetails[]) => {
        if (!arr) return;

        const idx = arr.findIndex(
          (applicant) => applicant.id === action.payload.id,
        );
        if (idx !== -1) {
          arr[idx] = {
            ...arr[idx],
            deductibleAmount: action.payload.deductibleAmount,
          };
        }
      };

      updateDeductible(state.quickQuoteData.applicantData);
      updateDeductible(state.applicants);
    },
    updateAllDeductibleAmount: (state, action: PayloadAction<any>) => {
      const data: ApplicantDetails[] = state.quickQuoteData.applicantData;
      const applicantData = state.applicants;

      if (data) {
        data.forEach((applicant) => {
          applicant.deductibleAmount = action.payload;
        });
      }
      if (applicantData) {
        applicantData.forEach((applicant) => {
          applicant.deductibleAmount = action.payload;
        });
      }
    },
    resetPlanSummaryState: (state) => {
      state.quickQuoteData = initialState.quickQuoteData;
    },
    updateOccupation: (
      state,
      action: PayloadAction<{ id: number; code: string; desc: string }>,
    ) => {
      const person = state.quickQuoteData.applicantData.find(
        (app) => app.id === action.payload.id,
      );
      if (person) {
        person.occupationCode = action.payload.code;
        person.occupationDesc = action.payload.desc;
      }
    },
    updateChildEmail: (state, action: PayloadAction<any>) => {
      const children = state.quickQuoteData.applicantData.filter(
        (app) => app.relationship === Relationship.Child,
      );
      if (children.length > 0) {
        children.forEach((child) => {
          child.emails = action.payload;
        });
      }
    },
    updateNricNumber: (
      state,
      action: PayloadAction<{ id: number; nric: string }>,
    ) => {
      const person = state.quickQuoteData.applicantData.find(
        (app) => app.id === action.payload.id,
      );
      if (person) {
        person.idNo = action.payload.nric;
      }
    },
    updateQuickQuote: (state, action: PayloadAction<any>) => {
      state.quickQuoteData.appId = action.payload.appId;
      state.quickQuoteData.refId = action.payload.refId;
      state.referenceId = action.payload.refId;
      state.premiumAmount.monthlyTotalPremium = action.payload.monthlyPremium;
      state.premiumAmount.yearlyTotalPremium = action.payload.yearlyPremium;
    },
    updateFamilyIndicator: (state, action: PayloadAction<any>) => {
      state.quickQuoteData.family = action.payload;
    },
    updatePersonalDetails: (state, action: PayloadAction<any>) => {
      state.payout = action.payload.payoutDetails;
      state.paymentMode = state.quickQuoteData.paymentMode;
      state.applicants = state.quickQuoteData.applicantData;
      state.appId = state.quickQuoteData.appId;
      state.referenceId = state.quickQuoteData.refId;
      const stripBase64 = (data: string) =>
        data?.replace(/^data:image\/\w+;base64,/, '');

      if (state.quickQuoteData.family) {
        state.family = state.quickQuoteData.family;
      }
      state.consentConfirmation = false;
      // const ownerId = state.applicants.filter(
      //   (app) => app.relationship === Relationship.Self,
      // );
      const applicants: ApplicantPersonalInfo[] = state.applicants;

      action.payload.applicantsInfo.forEach(({ details }) => {
        const applicantIndex = applicants.findIndex(
          (app) => app.id.toString() === details.id,
        );
        if (applicantIndex !== -1) {
          const applicant = applicants[applicantIndex];

          const contactInfo = {
            type: 'Mobile',
            value: PRE_PHONE_NUMBER + details.mobileNo,
            isPrimary: 'Y',
          };

          const income = {
            amount: Number(details.income ?? 0),
            range: '',
          };

          applicant.name = {
            fullName: capitalizeFirstLetterOfEachWord(details.fullname),
          };

          applicant.emails = [
            {
              type: 'email',
              value: details.email,
              isPrimary: 'Y',
            },
          ];

          applicant.contacts = [contactInfo];
          applicant.benePercentage = 0;

          applicant.title =
            applicant.relationship === Relationship.Self
              ? applicant.gender === GENDER_MALE
                ? TitleEnum.Mr
                : TitleEnum.Mrs
              : applicant.relationship === Relationship.Spouse
              ? applicant.gender === GENDER_MALE
                ? TitleEnum.Mr
                : TitleEnum.Mrs
              : applicant.relationship === Relationship.Child
              ? applicant.gender === GENDER_MALE
                ? TitleEnum.Mr
                : TitleEnum.Ms
              : TitleEnum.Other;

          applicant.pdpaConsent = 'N';
          applicant.isBumiputra = details.race === '001';
          applicant.isStaff = details.isStaff || null;
          applicant.isMaybankAcctHolder = details.isMaybankAcctHolder || null;
          applicant.isBeneficialOwner = details.isBeneficialOwner || false;
          applicant.race = details.race;
          applicant.religion = details.religion ?? null;
          applicant.monthlyIncome = income;
          applicant.maritalStatus =
            applicant.relationship !== Relationship.Child
              ? MARITAL_STATUS_OTHERS
              : details.marital ?? null;
          applicant.educationLevel = details.educationLevel || null;
          applicant.companyName = details.employer;
          applicant.nationality = details.nationality ?? 'MYS';
          applicant.addresses = details.addresses ?? [];
          applicant.contacts = [contactInfo];
          applicant.fundSource = details.fundSource;
          applicant.fundSourceOthers =
            details.fundSource === FUND_OTHERS
              ? capitalizeFirstLetterOfEachWord(details.fundSourceOthers)
              : null;
          applicant.wealthSource = details.wealthSource;
          applicant.uploadedNRIC = {
            frontSideImg: stripBase64(details.frontSide),
            backSideImg: stripBase64(details.backSide),
          };
        }
      });
    },
    updateCollection: (state, action: PayloadAction<Partial<Collection>>) => {
      state.collection = {
        ...state.collection,
        ...action.payload,
      };
    },
    updateApproval: (
      state,
      action: PayloadAction<Pick<Collection, 'approvalCode' | 'approvalDate'>>,
    ) => {
      if (state.collection) {
        state.collection.approvalCode = action.payload.approvalCode;
        state.collection.approvalDate = action.payload.approvalDate;
      }
    },
    updateSuppression: (state, action: PayloadAction<Suppression>) => {
      const payload: Suppression = {
        ePolicyPack: action.payload.ePolicyPack,
        notification: action.payload.notification,
      };
      state.suppression = payload;
    },
    mapGetApplicationsData: (
      state,
      action: PayloadAction<
        GetApplicationResponseWrapper<GetApplicationsResponse>
      >,
    ) => {
      const payload: GetApplicationResponseWrapper<GetApplicationsResponse> =
        action.payload;
      state.referenceId = payload.refId ?? '';
      state.applicants = [...payload.data.applicants];
      state.paymentMode = payload.data.paymentMode;
      state.productCode = payload.data.productCode;
      state.distributionChannel = payload.data.distributionChannel;
      state.preferredLang = payload.data.preferredLang;
      state.payMethod = payload.data.payMethod;
      state.promoCode = payload.data.promoCode;
      state.premiumAmount.monthlyTotalPremium =
        payload.data.monthlyTotalPremium;
      state.premiumAmount.yearlyTotalPremium = payload.data.yearlyTotalPremium;
      state.emailSent = payload.data.emailSent;
      state.consentConfirmation = payload.data.consentConfirmation;
      state.agent = payload.data.agent;
      state.employee = payload.data.employee;
      state.collection = payload.data.collection;
      state.payout = payload.data.payout;
      state.suppression = payload.data.suppression;
      state.purposeOfTrx = payload.data.purposeOfTrx;
      state.applicants = payload.data.applicants;
      state.family = payload.data.family;
    },
    mapApplicationsData: (
      state,
      action: PayloadAction<ApplicationsResponseWrapper<ApplicationsResponse>>,
    ) => {
      const payload: ApplicationsResponseWrapper<ApplicationsResponse> =
        action.payload;
      state.referenceId = payload.refId ?? '';
      state.applicationsApplicants = payload.data;
      state.premiumAmount.monthlyTotalPremium =
        payload.data.monthlyTotalPremium;
      state.premiumAmount.yearlyTotalPremium = payload.data.yearlyTotalPremium;
      state.premiumAmount.annualTotalPremium = payload.data.annualTotalPremium;
    },
    updateMarketingConsent: (state, action) => {
      state.marketingConsent = action.payload;
      state.applicants.forEach((appl) => {
        appl.pdpaConsent = action.payload;
      });
    },
    updateDeclaration: (state, action) => {
      state.declaration = action.payload;
    },
    resetPlanCode: (state) => {
      state.quickQuoteData.applicantData.forEach((applicant) => {
        applicant.planCode = '';
        applicant.deductibleAmount = 0;
      });
      delete state.family;
    },
    updatePlanSummary: (state) => {
      // Step 1: Get valid applicant IDs from quickQuoteData
      const validApplicantIds = state.quickQuoteData.applicantData.map(
        (a) => a.id,
      );
      // Step 2: Filter out applicants not present in quickQuoteData
      state.applicants = state.applicants.filter((app) =>
        validApplicantIds.includes(app.id),
      );

      state.applicants.forEach((app) => {
        const quickQuotePlan = state.quickQuoteData.applicantData.find(
          (planApplicant) => planApplicant.id === app.id,
        );
        if (quickQuotePlan) {
          app.planCode = quickQuotePlan.planCode;
          app.deductibleAmount = quickQuotePlan.deductibleAmount;
        }
      });

      state.paymentMode = state.quickQuoteData.paymentMode;
      // Step 5: If only one applicant is PO, remove family section
      if (state.applicants.length === 1) {
        delete state.family;
      }
    },
    updateClientInfo: (
      state,
      action: PayloadAction<{
        clientInfo: any[];
        clientAddress: Addresses[];
        clientType: string;
      }>,
    ) => {
      const clientDetailsArray = action.payload.clientInfo;
      const stripBase64 = (data: string) =>
        data?.replace(/^data:image\/\w+;base64,/, '');
      clientDetailsArray.forEach((clientDetails) => {
        if (clientDetails.id) {
          const contactInfo = {
            type: 'Mobile',
            value: PRE_PHONE_NUMBER + clientDetails.mobileNo,
            isPrimary: 'Y',
          };

          const nricNumber = clientDetails.nric;
          let benefitGender;

          if (action.payload.clientType === ClientType.Beneficiary) {
            const lastDigit = parseInt(nricNumber.charAt(11));

            if (lastDigit % 2 === 0) {
              benefitGender = GENDER_FEMALE;
            } else {
              benefitGender = GENDER_MALE;
            }
          }

          const income = {
            amount: Number(clientDetails.income ?? 0) ?? 0,
            range: '',
          };
          const ownerDetail = state.applicants.find(
            (appl) => appl.id === SELF_ID,
          );
          const gender =
            action.payload.clientType === ClientType.Beneficiary
              ? benefitGender
              : clientDetails.gender;
          const uploadNric: UploadedNRIC =
            action.payload.clientType === ClientType.Beneficiary
              ? undefined
              : {
                  frontSideImg: stripBase64(clientDetails.frontSide),
                  backSideImg: stripBase64(clientDetails.backSide),
                };
          const client: ApplicantPersonalInfo = {
            ...state.applicants[clientDetails.id],
            id:
              typeof clientDetails.id === 'string'
                ? Number(clientDetails.id)
                : clientDetails.id,
            idType: state.distributionChannel,
            idNo: nricNumber,
            dob: setDOB(nricNumber)!,
            planCode: ownerDetail?.planCode!,
            name: {
              fullName: capitalizeFirstChar(clientDetails.fullname),
            },
            emails: [
              {
                type: 'email',
                value:
                  action.payload.clientType === ClientType.Beneficiary
                    ? ownerDetail?.emails[0].value
                    : clientDetails.email,
                isPrimary: 'Y',
              },
            ],
            clientType: action.payload.clientType,
            relationship: clientDetails.relationship,
            occupationCode: clientDetails.occupation,
            gender,
            title: gender === GENDER_MALE ? TitleEnum.Mr : TitleEnum.Mrs,
            pdpaConsent: state.marketingConsent,
            isBeneficialOwner:
              action.payload.clientType === ClientType.Beneficiary,
            race: clientDetails.race ?? null,
            religion: clientDetails.religion ?? null,
            monthlyIncome: income,
            maritalStatus: clientDetails.marital ?? null,
            companyName: clientDetails.employer,
            nationality: clientDetails.nationality,
            nationalityDesc: clientDetails.nationalityDesc,
            addresses: action.payload.clientAddress,
            contacts: [contactInfo],
            uploadedNRIC: uploadNric,
          };
          if (state.applicants.every((appl) => appl.id !== client.id)) {
            state.applicants.push(client);
          }
        }
      });
    },
    updatePremiumAmount: (state, action: PayloadAction<any>) => {
      state.premiumAmount.monthlyTotalPremium =
        action.payload.monthlyTotalPremium;
      state.premiumAmount.yearlyTotalPremium =
        action.payload.yearlyTotalPremium;
    },
    updatePurposeOfTransaction: (
      state,
      action: PayloadAction<PurposeOfTransaction>,
    ) => {
      state.purposeOfTrx = action.payload;
    },
    updatePromoCode: (state, action: PayloadAction<PromoCodeForm>) => {
      state.promoCode = action.payload.promoCode;
    },
    resetAgentVerification: (state) => {
      state.isValidAgent = undefined;
    },
    updateDistributionChannel: (
      state,
      action: PayloadAction<DistributionChannel>,
    ) => {
      state.distributionChannel = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPremiumQuotation.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getPremiumQuotation.fulfilled, (state, action) => {
        state.planData.applicants = action.payload.data.applicants;
        state.loading = false;
        state.error = false;
      })
      .addCase(getPremiumQuotation.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });

    builder
      .addCase(verifyAgent.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(verifyAgent.fulfilled, (state, action) => {
        state.isValidAgent =
          action.payload?.data?.status === AgentStatus.Active;
        state.loading = false;
        state.error = false;
      })
      .addCase(verifyAgent.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });
    builder
      .addCase(documents.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(documents.fulfilled, (state) => {
        state.loading = false;
        state.error = false;
      })
      .addCase(documents.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });
  },
});

export const {
  updateApplicantDetails,
  removeApplicant,
  updateQuoteFormDetail,
  addPlanData,
  addPlanDataDeductible,
  updatePlanSelection,
  updatePlanSummary,
  updatePaymentMode,
  updatePlanCode,
  updateAllPlanCode,
  updateDeductibleAmount,
  updateAllDeductibleAmount,
  updateQuickQuote,
  updateChildEmail,
  removeChildApplicant,
  updatePersonalDetails,
  updateOccupation,
  updateNricNumber,
  resetPlanSummaryState,
  updateCollection,
  updateSuppression,
  updateconsentConfirmation,
  mapGetApplicationsData,
  mapApplicationsData,
  updateMarketingConsent,
  updateDeclaration,
  updateApproval,
  updateProductCode,
  updateAgentCode,
  resetPlanCode,
  updateFamilyIndicator,
  updateClientInfo,
  updatePremiumAmount,
  updatePurposeOfTransaction,
  updatePromoCode,
  resetAgentVerification,
  updateDistributionChannel,
} = planSummarySlice.actions;

export default planSummarySlice.reducer;
